import React from 'react';
import {
  FileText,
  ShieldCheck,
  ArrowLeft,
  Scale,
  BookOpen,
  UserCheck,
  AlertTriangle,
  Lock,
  Globe,
  Mail,
  Calendar,
} from 'lucide-react';

interface TermsOfUseViewProps {
  onBack: () => void;
}

export const TermsOfUseView: React.FC<TermsOfUseViewProps> = ({ onBack }) => {
  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8 text-[#FAFAFA] box-border flex-1 flex flex-col">
      {/* Top Navigation Bar */}
      <div className="flex items-center justify-between gap-4 border-b border-[#262626] pb-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[#141414] border border-[#404040] hover:bg-[#1C1C1C] text-xs font-semibold text-[#FAFAFA] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>
        <div className="flex items-center gap-2 text-xs font-mono text-[#A3A3A3]">
          <Calendar className="w-3.5 h-3.5 text-[#A3A3A3]" />
          <span>Effective Date: [EFFECTIVE DATE: October 31, 2026]</span>
        </div>
      </div>

      {/* Hero Header */}
      <div className="space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-[#1C1C1C] text-[#A3A3A3] border border-[#525252]">
          <Scale className="w-3.5 h-3.5" />
          <span>Legal Agreement</span>
        </div>
        <h1 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
          Terms of Use &amp; End-User License Agreement (EULA)
        </h1>
        <p className="text-sm sm:text-base text-[#A3A3A3] leading-relaxed">
          Please read these Terms of Use and End-User License Agreement carefully before creating an account or playing on Quick!Bible.
        </p>
      </div>

      {/* Main Content Sections */}
      <div className="space-y-6 text-sm leading-relaxed text-[#D4D4D4]">
        {/* Section 1: Identity & Hobby-Project Status */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-[#262626] text-[#A3A3A3] flex items-center justify-center text-xs font-mono">1</span>
            Identity &amp; Hobby-Project Status
          </h2>
          <p>
            Quick!Bible (available online at <a href="https://quickibible.com" className="text-[#A3A3A3] hover:underline">https://quickibible.com</a>) is a personal, non-commercial hobby project owned and developed solely by <strong>Adonis Melencion</strong>.
          </p>
          <p>
            This application is created as a labor of love for biblical education, fellowship, and scripture study. It is not an enterprise commercial service, and its operation relies on personal development resources and third-party cloud infrastructure.
          </p>
        </section>

        {/* Section 2: Service Description & Availability */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-[#262626] text-[#A3A3A3] flex items-center justify-center text-xs font-mono">2</span>
            Service Description &amp; Feature Availability
          </h2>
          <p>
            Quick!Bible provides interactive Bible trivia quizzes, speed competitions, daily challenges, multiplayer matchmaking, and live event hosting studio features designed to encourage Scripture study.
          </p>
          <p>
            The service is provided on an <strong>&ldquo;as-is&rdquo;</strong> and <strong>&ldquo;as-available&rdquo;</strong> basis. Adonis Melencion reserves the right to modify, adjust, update, temporarily suspend, or discontinue any game mode, feature, leaderboard, question set, or service capability at any time without prior notice or liability.
          </p>
        </section>

        {/* Section 3: Account Responsibility */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-[#262626] text-[#A3A3A3] flex items-center justify-center text-xs font-mono">3</span>
            Account Registration &amp; Security Responsibility
          </h2>
          <p>
            When registering an account, you agree to provide accurate, current, and truthful registration information.
          </p>
          <p>
            You are solely responsible for maintaining the confidentiality of your account credentials and for all activities, gameplay, or submissions that occur under your account. You agree to notify the owner immediately of any unauthorized use or security breach.
          </p>
        </section>

        {/* Section 4: Limited License */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-[#262626] text-[#A3A3A3] flex items-center justify-center text-xs font-mono">4</span>
            Limited End-User License (EULA)
          </h2>
          <p>
            Subject to your continuous compliance with these Terms, Adonis Melencion grants you a personal, revocable, non-exclusive, non-transferable, non-sublicensable limited license to access and use Quick!Bible strictly for personal, non-commercial biblical study, educational, and recreational purposes.
          </p>
          <p>
            This license does not convey any ownership interest in the application or its intellectual property, and may be revoked if these Terms are violated.
          </p>
        </section>

        {/* Section 5: Ownership & Intellectual Property */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-[#262626] text-[#A3A3A3] flex items-center justify-center text-xs font-mono">5</span>
            Ownership &amp; Intellectual Property
          </h2>
          <p>
            All original application code, software architecture, user interface designs, logos, graphic assets, typography styles, sound arrangements, and proprietary content are the exclusive intellectual property of Adonis Melencion or respective third-party licensors, unless otherwise stated.
          </p>
          <p className="bg-[#141414] p-3 rounded-xl border border-[#262626] text-xs text-[#A3A3A3]">
            <strong>Important Bible Content Clarification:</strong> Quick!Bible does NOT claim ownership of the sacred texts of the Holy Scriptures or third-party Bible translations. Biblical passages, chapter references, and historical scripture texts belong to their respective public-domain or copyright holders.
          </p>
        </section>

        {/* Section 6: Restrictions on Use */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-[#262626] text-[#A3A3A3] flex items-center justify-center text-xs font-mono">6</span>
            Restrictions on Use
          </h2>
          <p>
            Except to the extent expressly permitted by applicable mandatory law or with prior written consent from Adonis Melencion, you agree that you shall NOT:
          </p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-[#D4D4D4]">
            <li>Copy, reproduce, republish, redistribute, sublicense, sell, rent, lease, or commercially exploit any part of Quick!Bible;</li>
            <li>Modify, adapt, translate, reverse engineer, decompile, or disassemble the source code or underlying algorithms;</li>
            <li>Remove, obscure, or alter any copyright notices, trademarks, or proprietary markings;</li>
            <li>Access or attempt to access server infrastructure, databases, API keys, or accounts without authorization;</li>
            <li>Create derivative software, mirror services, or cloned trivia platforms based on Quick!Bible.</li>
          </ul>
        </section>

        {/* Section 7: Proper Use & Prohibited Conduct */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-[#262626] text-[#A3A3A3] flex items-center justify-center text-xs font-mono">7</span>
            Fair Play &amp; Prohibited Conduct
          </h2>
          <p>
            Quick!Bible is dedicated to edifying Scripture engagement. The following behaviors are strictly prohibited:
          </p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-[#D4D4D4]">
            <li><strong>Cheating &amp; Exploitation:</strong> Using bots, automated response scripts, client memory injection, timing hacks, or exploiting software bugs to artificially inflate XP, scores, or leaderboard rankings;</li>
            <li><strong>Multiplayer Disruption:</strong> Intentionally disconnecting, spamming socket connections, flooding rooms, or degrading the gaming experience of other scholars;</li>
            <li><strong>Harassment &amp; Impersonation:</strong> Using offensive, profane, or derogatory usernames, or impersonating other players, tournament hosts, or platform administrators;</li>
            <li><strong>Infrastructure Abuse:</strong> Performing denial-of-service attacks, rate-limit circumvention, vulnerability scanning, or sending unauthorized network payloads.</li>
          </ul>
        </section>

        {/* Section 8: User Submissions */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-[#262626] text-[#A3A3A3] flex items-center justify-center text-xs font-mono">8</span>
            User Submissions &amp; Profile Information
          </h2>
          <p>
            Users may enter custom profile content (such as bio, favorite scripture verse, church/organization affiliation, or custom live event titles).
          </p>
          <p>
            By entering or submitting such content, you grant Adonis Melencion a limited, worldwide, royalty-free, non-exclusive license solely to host, display, format, and transmit that content as necessary to operate the application&apos;s features. You represent that your submissions do not infringe any third-party rights or violate applicable laws.
          </p>
        </section>

        {/* Section 9: Suspension & Termination */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-[#262626] text-[#A3A3A3] flex items-center justify-center text-xs font-mono">9</span>
            Suspension &amp; Account Termination
          </h2>
          <p>
            Adonis Melencion reserves the right to suspend or terminate your account, revoke your license to use the app, or block network access if you engage in serious or repeated violations of these Terms, engage in fraudulent or abusive conduct, or if necessary to safeguard the community and platform security, subject to applicable law.
          </p>
        </section>

        {/* Section 10: Disclaimers & Limitation of Liability */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-[#262626] text-[#A3A3A3] flex items-center justify-center text-xs font-mono">10</span>
            Warranty Disclaimers &amp; Limitation of Liability
          </h2>
          <p>
            To the maximum extent permitted by applicable law, Quick!Bible is provided without warranties of any kind, whether express, statutory, or implied. The developer does not guarantee that the app will always be error-free, uninterrupted, or entirely secure.
          </p>
          <p>
            In no event shall Adonis Melencion be liable for indirect, incidental, special, consequential, or punitive damages resulting from your access to or inability to use Quick!Bible. Nothing in these Terms shall limit or exclude any statutory consumer rights that cannot legally be waived under applicable law.
          </p>
        </section>

        {/* Section 11: Third-Party Bible Content */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-[#262626] text-[#A3A3A3] flex items-center justify-center text-xs font-mono">11</span>
            Third-Party Bible Translations
          </h2>
          <p>
            Scripture quotations, book names, and biblical verses are referenced for educational, theological, and devotional purposes. Public domain translations (such as the King James Version) and fair-use educational references are utilized.
          </p>
          <p>
            Third-party Bible materials remain subject to their respective rights and licenses. Quick!Bible acknowledges and honors the sacred heritage and separate copyright terms of third-party translations where applicable.
          </p>
        </section>

        {/* Section 12: Contact Information */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-[#262626] text-[#A3A3A3] flex items-center justify-center text-xs font-mono">12</span>
            Contact Information
          </h2>
          <p>
            If you have questions or feedback regarding these Terms of Use and EULA, please contact the developer:
          </p>
          <div className="p-3.5 rounded-xl bg-[#141414] border border-[#262626] space-y-1 text-xs font-mono text-[#D4D4D4]">
            <div><strong>Developer:</strong> Adonis Melencion</div>
            <div><strong>Project:</strong> Quick!Bible (Personal Hobby Project)</div>
            <div><strong>Website:</strong> https://quickibible.com</div>
            <div className="text-amber-400 font-semibold">
              <strong>Contact Email:</strong>{' '}
              <a href="mailto:adonizchoy@gmail.com" className="underline hover:text-amber-300 transition-colors">
                adonizchoy@gmail.com
              </a>
            </div>
          </div>
        </section>
      </div>

      {/* Footer Back Button */}
      <div className="pt-4 border-t border-[#262626] flex justify-center">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-xs font-semibold shadow-md transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Return to Quick!Bible</span>
        </button>
      </div>
    </div>
  );
};
